import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, Phone } from 'lucide-react';

const LOGO_URL = `/images/logo.png`;

export const Header = () => {
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isHome = location.pathname === '/';

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/products', label: t('nav.products') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const languages = [
    { code: 'nl', label: 'NL' },
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
    { code: 'de', label: 'DE' },
  ];

  const headerBg = scrolled || !isHome
    ? 'bg-white border-b border-gray-200'
    : 'bg-transparent';

  const textColor = scrolled || !isHome ? 'text-slate-900' : 'text-white';
  const mutedColor = scrolled || !isHome ? 'text-slate-500' : 'text-white/60';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`} data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Alphaprofiles"
              className={`h-14 w-auto object-contain transition-all duration-300 ${
                !scrolled && isHome ? 'brightness-0 invert' : ''
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`text-[13px] font-medium tracking-wide uppercase transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-[#BE1622]'
                    : `${textColor} hover:text-[#BE1622]`
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-5">
            <div className={`flex items-center text-[11px] font-medium gap-1 ${mutedColor}`}>
              {languages.map((lang, index) => (
                <React.Fragment key={lang.code}>
                  <button
                    onClick={() => setLanguage(lang.code)}
                    className={`transition-colors px-1 ${
                      language === lang.code
                        ? 'text-[#BE1622] font-bold'
                        : 'hover:opacity-80'
                    }`}
                    data-testid={`lang-btn-${lang.code}`}
                  >
                    {lang.label}
                  </button>
                  {index < languages.length - 1 && <span className="opacity-30">|</span>}
                </React.Fragment>
              ))}
            </div>

            <Link to="/contact">
              <button
                className="inline-flex items-center gap-2 bg-[#BE1622] hover:bg-[#9E111B] text-white text-[12px] font-semibold tracking-wider uppercase px-5 py-2.5 transition-all duration-200"
                data-testid="header-cta"
              >
                <Phone className="w-3.5 h-3.5" />
                {t('nav.contact')}
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 ${textColor}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <>
            <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileMenuOpen(false)} />
            <div className="lg:hidden fixed top-0 left-0 bottom-0 w-72 bg-white z-50 shadow-xl">
              <div className="flex items-center justify-between p-5 border-b border-gray-200">
                <img src={LOGO_URL} alt="Alphaprofiles" className="h-8 w-auto object-contain" />
                <button onClick={() => setMobileMenuOpen(false)} className="p-1 text-slate-500"><X size={20} /></button>
              </div>
              <nav className="flex flex-col p-4 gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-[13px] font-medium tracking-wide uppercase py-3 px-4 transition-colors ${
                      isActive(link.path) ? 'text-[#BE1622] bg-red-50/50' : 'text-slate-800 hover:text-[#BE1622]'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
                <div className="flex items-center justify-center gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setMobileMenuOpen(false); }}
                      className={`px-3 py-1.5 text-[11px] font-semibold tracking-wide transition-all ${
                        language === lang.code ? 'bg-[#BE1622] text-white' : 'bg-gray-100 text-slate-600'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
};
